import { useProjectLocalStore } from "./use-project-local-store";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PosterProject } from "PosterEditor/model/poster-project";
import { useCallback, useRef, useEffect } from "react";
import { useProjectRemoteStore } from "./use-project-remote-store";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { migrateProject } from "PosterEditor/model/project-migrator";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { v4 as uuid } from "uuid";
import { useImageStore } from "entity-store/image-store/use-image-store";

export const useProjectStore = () => {
  const queryClient = useQueryClient();
  const { isUserAuthenticated } = useAuthContext();
  const {
    loadProjects: loadProjectsLocal,
    loadProject: loadProjectLocal,
    saveProject: saveProjectLocal,
    createProject: createProjectLocal,
    deleteProject: deleteProjectLocal,
    deleteAllProjects: deleteAllProjectsLocal,

    draftProject,
    existingDraft,
    deleteDraft,
  } = useProjectLocalStore();
  const {
    save: saveRemote,
    fetchAllProjects: fetchRemote,
    fetchProjectsInCheckout: fetchCheckoutProjectsRemote,
    remove: removeRemote,
    removeAllNotInCheckout: removeAllNotInCheckoutRemote,
    deleteDataAssociatedWithProject,
  } = useProjectRemoteStore();
  const { processPendingUploads } = useImageStore();

  const saveCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const pendingProjectRef = useRef<PosterProject | null>(null);

  const {
    data: projects,
    refetch: refetchProjects,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      if (isUserAuthenticated) {
        const remoteData = await fetchRemote();
        if (remoteData) {
          remoteData.forEach((remoteProject) => {
            const migrated = migrateProject(remoteProject);
            saveProjectLocal(migrated);
          });
        }
      }
      const data = await loadProjectsLocal();
      return data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const loadProjects = (projectIDs: string[]) => {
    return projectIDs
      .map((projectId) => {
        return loadProjectLocal(projectId);
      })
      .filter((project) => project !== undefined) as PosterProject[];
  };

  const testForDuplicates = (id: String) => {
    const projects = loadProjectsLocal();
    const matches = projects.filter((project) => project.id === id);
    return matches;
  };

  const loadProject = (projectId: string) => {
    const project = loadProjectLocal(projectId);
    return project;
  };

  //TODO: proper implementation when backend is up.
  const fetchProject = async (id: string) => {
    return queryClient.fetchQuery({
      queryKey: ["project", id],
      queryFn: async () => await loadProjectLocal(id),
      staleTime: 1000 * 60 * 5,
    });
  };

  //TODO: proper implementation when backend is up.
  const putProject = useMutation({
    mutationFn: async (project: PosterProject) =>
      await saveRemote.mutateAsync(project),
  });

  const saveProject = useCallback(
    (project: PosterProject, forceImmediateRemoteStore?: boolean) => {
      const updated = { ...project, updatedAt: new Date() };
      saveProjectLocal(updated);
      queryClient.setQueryData(["projects"], (oldData?: PosterProject[]) => {
        if (!oldData) return [updated]; // If no data, initialize with this project
        return oldData.map((p) => (p.id === updated.id ? updated : p));
      });
      if (!isUserAuthenticated) {
        return;
      }
      if (!project.isTemplateDraft) {
        pendingProjectRef.current = updated; // Track pending project
        if (forceImmediateRemoteStore) {
          saveRemote.mutate(updated);
          pendingProjectRef.current = null;
        }
        if (saveCooldownRef.current) {
          clearTimeout(saveCooldownRef.current);
        }
        saveCooldownRef.current = setTimeout(() => {
          saveRemote.mutate(updated);
          saveCooldownRef.current = null;
          pendingProjectRef.current = null;
        }, 10000);
      }
    },
    [saveRemote, queryClient, saveProjectLocal, isUserAuthenticated]
  );

  // Flush pending saves when page becomes hidden or before unload
  useEffect(() => {
    const flushPendingSave = () => {
      if (pendingProjectRef.current && saveCooldownRef.current) {
        clearTimeout(saveCooldownRef.current);
        saveCooldownRef.current = null;
        saveRemote.mutate(pendingProjectRef.current);
        pendingProjectRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      console.log("visibility change detected");
      if (document.hidden) {
        flushPendingSave();
      }
    };

    const handleBeforeUnload = () => {
      flushPendingSave();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveRemote]);

  const createProject = useMutation({
    mutationFn: async () => {
      const project = await createProjectLocal();
      await putProject.mutateAsync(project);
      return project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const loadDraftProject = (createNew: boolean, template: ProjectTemplate) => {
    const draft = draftProject(createNew, template);
    removeAllNotInCheckoutRemote(draft.id, projects ?? []);
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    return draft;
  };

  const projectsNotInCheckout = (): PosterProject[] => {
    const filtered = projects?.filter(
      (p) => p.isInCheckout === false && p.isOrdered === false
    );
    return filtered ?? [];
  };

  const deleteProjectMutation = useMutation({
    mutationFn: async (project: PosterProject) => {
      // Only delete from remote if user is authenticated AND project is not guest_project
      if (isUserAuthenticated && project.id !== "guest_project") {
        await deleteDataAssociatedWithProject(project);
        await removeRemote.mutateAsync(project.id);
      }
      // Always delete from local storage
      await deleteProjectLocal(project);
    },
    onSuccess: async () => {
      // Force immediate refetch and wait for it to complete
      await queryClient.refetchQueries({ queryKey: ["projects"] });
    },
  });

  const deleteProject = (project: PosterProject) =>
    deleteProjectMutation.mutate(project);

  const deleteProjectAsync = (project: PosterProject) =>
    deleteProjectMutation.mutateAsync(project);

  const clearLocalCache = () => {
    deleteAllProjectsLocal();
  };

  const saveGuestProjectToUsersAccount = async () => {
    if (!isUserAuthenticated) {
      return;
    }
    const project = loadProject("guest_project");

    if (project) {
      const newID = uuid();
      console.log(`Migrating guest project to authenticated account. Old ID: guest_project, New ID: ${newID}`);

      // Process pending image uploads BEFORE saving the project
      try {
        await processPendingUploads(newID);
        console.log('Successfully processed pending image uploads');
      } catch (error) {
        console.error('Failed to process pending image uploads:', error);
        // Continue anyway - images can be re-uploaded manually if needed
      }

      const updated = { ...project, id: newID, isTemplateDraft: false };
      // Delete the guest_project from local storage
      deleteProjectLocal(project);
      // Also delete any existing draft to ensure clean state
      deleteDraft();
      saveProject(updated, true);
      return updated.id;
    }

    console.warn('No guest project found to migrate');
    return;
  };

  const markProjectOrdered = (projectID: string) => {
    const project = loadProject(projectID);
    if (project) {
      const updated = { ...project, isOrdered: true };
      saveProject(updated, true);
    }
  };

  const removeAllNotInCheckout = async (exceptID: string | undefined) => {
    await removeAllNotInCheckoutRemote(exceptID, projects ?? []);
  };

  const {
    data: checkoutProjects,
    refetch: refetchCheckoutProjects,
    isLoading: isCheckoutLoading,
  } = useQuery({
    queryKey: ["checkoutProjects"],
    queryFn: async () => {
      if (!isUserAuthenticated) {
        return [];
      }
      return await fetchCheckoutProjectsRemote();
    },
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    enabled: isUserAuthenticated,
  });

  return {
    // Cached projects list.
    // This is populated from remote store whn this hook is mounted.
    projects,

    // Checkout projects from Supabase
    checkoutProjects,
    refetchCheckoutProjects,
    isCheckoutLoading,

    draftProject: loadDraftProject,

    // Loads single project from local storage synchronously.
    // Use if you're sure the project is fetched with fetchProject.
    loadProject,

    // Loads multiple projects from local storage synchronously.
    // Use if you're sure the projects are fetched with fetchProject.
    loadProjects,

    // Local projects not in checkout.
    projectsNotInCheckout,

    // Loads single projct from remote and saves it locally.
    fetchProject,
    // Saves the project locally.
    saveProject,
    // Saves the project to remote asynchronously.
    putProject,
    // Async create function.
    createProject,
    // Async delete function.
    deleteProject,
    deleteProjectAsync,

    // Manual refresh trigger
    refetchProjects,

    existingDraft,
    deleteDraft,

    clearLocalCache,

    saveGuestProjectToUsersAccount,

    markProjectOrdered,

    isLoading,
    isFetching,
    isError,

    testForDuplicates,

    removeAllNotInCheckout,
  };
};
